<script lang="ts">
  import moment from 'moment';
  import { onDestroy, onMount } from 'svelte';
  import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
  } from 'chart.js';
  import { Chart } from 'svelte-chartjs';
  import zoomPlugin from 'chartjs-plugin-zoom';
  import Table from '../Table.svelte';
  import { options } from '../../configs/BarOptions';
  import DateRange from '../common/DateRange.svelte';
  import { squezzeBranches } from '../../utils/branches';
  import { colors, resetChart } from '../../utils/common';
  import { requestsParams } from '../../configs/Requests';
  import loader from 'src/components/cabinet/#assets/loader.svg';
  import { getGooglePlayAnalytics } from '../../../#services/googlePlayAnalytics';
  import { getMathRewardsAnalytics } from '../../../#services/mathRewardsAnalytics';
  import { getTransactionsAnalytics } from 'src/components/cabinet/#services/transactionsAnalytics';

  export let onlyChart = false;
  export let isPreview = false;

  let spinsBranch, spinsMathBranch, lootboxesBranch, lootboxesMathBranch;
  let questsAchievementsBranch, bingoTournamentsBranch, spendingBranch;
  let questsByMembershipsBranch, membershipsFromSiteBranch, googlePlayBranch, googlePlayPurchasesBranch, googlePlayOffersBranch, lisRewardsBranch;

  let chart;
  let creator = 'all';
  let userId = null;

  let labels = [];
  let datasets = [];

  let startDate = 'startDate'
  let endDate = 'endDate'

  if (onlyChart) {
    startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
    endDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
  }

  const minDate = '2022-12-30';
  const maxDate = moment().format('YYYY-MM-DD');

  const chartPlugins = [
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
  ]

  if (!onlyChart) {
    chartPlugins.push(zoomPlugin)
  }

  ChartJS.register(...chartPlugins)

  function refreshTransactionsAnalytics() {
    const requestData = requestsParams.main.hard({
      creator: creator !== 'all' ? creator : undefined,
      userId: userId || undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getTransactionsAnalytics.send({ data: requestData });
  }

  function refreshMathRewards() {
    const requestData = requestsParams.main.rewards({
      game: creator !== 'all' ? creator : undefined,
      userId: userId || undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getMathRewardsAnalytics.send({ data: requestData });
  }

  function refreshFromGP() {
    const requestData = requestsParams.main.googlePlay({
      game: creator !== 'all' ? creator : undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getGooglePlayAnalytics.send({ data: requestData });
  }

  function refresh() {
    resetChart(chart, labels, datasets);
    refreshTransactionsAnalytics();
    refreshMathRewards();
    refreshFromGP();
  }

  getTransactionsAnalytics.subscribe((value) => {
    try {
      if (value.hasValue && !value.error && !value.loading) {
        while (labels.length) {
          labels.pop();
        }

        while (datasets.length) {
          datasets.pop();
        } 

        value.value.branches[0].values.forEach(e => {
          labels.push(e.point);
        });

        spinsBranch = squezzeBranches([
          value.value.branches[0].values,
          value.value.branches[1].values,
          value.value.branches[2].values,
        ]);
        lootboxesBranch = value.value.branches[3].values;
        questsAchievementsBranch = value.value.branches[4].values;
        bingoTournamentsBranch = value.value.branches[5].values;
        spendingBranch = value.value.branches[6].values;
        membershipsFromSiteBranch = value.value.branches[7].values;
        lisRewardsBranch = value.value.branches[8].values;
      }
    } catch (e) {}
  });

  getMathRewardsAnalytics.subscribe((value) => {
    try {
      if (value.hasValue && !value.error && !value.loading) {
        spinsMathBranch = squezzeBranches([
          value.value.branches[0].values,
          value.value.branches[1].values
        ]).map((v) => ({ ...v, amount: v.amount * -1 }));
        lootboxesMathBranch = squezzeBranches([
          value.value.branches[2].values,
          value.value.branches[3].values
        ]).map((v) => ({ ...v, amount: v.amount * -1 }));
        questsByMembershipsBranch = value.value.branches[4].values.map((v) => ({ ...v, amount: v.amount * -1 }));
      }
    } catch (e) {}
  });

  getGooglePlayAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      try {
        googlePlayBranch = value.value.branches[0].values;
        googlePlayPurchasesBranch = value.value.branches[1].values;
        googlePlayOffersBranch = value.value.branches[2].values;
      } catch (e) {}
    }
  });

  onDestroy(() => {
    resetChart(chart);
    getTransactionsAnalytics.reset();
    getMathRewardsAnalytics.reset();
    getGooglePlayAnalytics.reset();
  });
  
  onMount(() => {
    refresh();
  });
</script>

{#if !onlyChart && !isPreview}

<div class="buttons">
  <DateRange bind:startDate={startDate} bind:endDate={endDate} {minDate} {maxDate} refreshCallback={() => refresh()} />
</div>
<hr>
<div class="buttons">
  <div class="pc-line">
    <span class="date-label">User</span>
    <input type="text" class="form-control" bind:value={userId} placeholder="Input UserID" on:input={refresh}>
  </div>
</div>
<hr>
<div class="buttons">
  <div class="pc-line">
      <button class="btn btn-primary btn-filter" on:click={refresh}>Refresh</button>
  </div>
</div>

{/if}

{#if !isPreview}

<p class="description">LIS Profit - LIS Rewards</p>

{/if}

<div class="graphic">
  {#if $getTransactionsAnalytics.error || $getMathRewardsAnalytics.error || $getGooglePlayAnalytics.error}
    {$getTransactionsAnalytics.error}
  {:else if $getTransactionsAnalytics.loading || $getMathRewardsAnalytics.loading || $getGooglePlayAnalytics.loading}
    <div class="graphic-loading">
      <img src="{loader}" alt="">
    </div>
  {:else if $getTransactionsAnalytics.value && $getMathRewardsAnalytics.value && $getGooglePlayAnalytics.value}
    {#if onlyChart && !isPreview}
      <p>Total saldo: { new Intl.NumberFormat('ja-JP').format(Math.trunc(squezzeBranches([
        spinsBranch,
        spinsMathBranch,
        lootboxesBranch,
        lootboxesMathBranch,
        bingoTournamentsBranch,
        questsAchievementsBranch,
        spendingBranch,
        questsByMembershipsBranch,
        membershipsFromSiteBranch,
        googlePlayBranch,
        googlePlayPurchasesBranch,
        googlePlayOffersBranch,
        lisRewardsBranch,
      ]).reduce((a, b) => a + b.amount, 0))) }</p>
      <p>Average saldo: { new Intl.NumberFormat('ja-JP').format(Math.trunc(squezzeBranches([
        spinsBranch,
        spinsMathBranch,
        lootboxesBranch,
        lootboxesMathBranch,
        bingoTournamentsBranch,
        questsAchievementsBranch,
        spendingBranch,
        questsByMembershipsBranch,
        membershipsFromSiteBranch,
        googlePlayBranch,
        googlePlayPurchasesBranch,
        googlePlayOffersBranch,
        lisRewardsBranch,
      ]).reduce((a, b) => a + b.amount, 0) / spinsBranch.length || 0)) }</p>
    {/if}
    <Chart bind:this={chart} type="bar" data={{ labels, datasets: [
      {
        label: 'Total Saldo, LIS',
        backgroundColor: colors[0],
        borderColor: colors[0],
        fill: false,
        data: squezzeBranches([
          spinsBranch,
          spinsMathBranch,
          lootboxesBranch,
          lootboxesMathBranch,
          bingoTournamentsBranch,
          questsAchievementsBranch,
          spendingBranch,
          questsByMembershipsBranch,
          membershipsFromSiteBranch,
          googlePlayBranch,
          googlePlayPurchasesBranch,
          googlePlayOffersBranch,
          lisRewardsBranch,
        ]).map(v => v.amount),
        stack: 0,
      }
    ] }} width={500} height={270} {options} />
    {#if !onlyChart && !isPreview}
      <Table branches={{
        'Spins, LIS': squezzeBranches([spinsBranch, spinsMathBranch]),
        'Lootboxes, LIS': squezzeBranches([lootboxesBranch, lootboxesMathBranch]),
        'Units & Boosters, LIS': spendingBranch,
        'Achievs. & Quests, LIS': questsAchievementsBranch,
        'Tourns. & Bingo, LIS': bingoTournamentsBranch,
        'Quests by Mem., LIS': questsByMembershipsBranch,
        'Mem. (from site), LIS': membershipsFromSiteBranch,
        'LIS from GP, LIS': lisRewardsBranch,
        'GP Tokens, LIS': googlePlayPurchasesBranch,
        'GP Offers, LIS': googlePlayOffersBranch,
        'GP Subs, LIS': googlePlayBranch,
        'Total Saldo, LIS': squezzeBranches([
          spinsBranch,
          spinsMathBranch,
          lootboxesBranch,
          lootboxesMathBranch,
          bingoTournamentsBranch,
          questsAchievementsBranch,
          spendingBranch,
          questsByMembershipsBranch,
          membershipsFromSiteBranch,
          googlePlayBranch,
          googlePlayPurchasesBranch,
          googlePlayOffersBranch,
          lisRewardsBranch,
        ])
      }} borders={[2,4,6,10]}
      isTableHidden={false}/>
    {/if}
  {/if}
</div>